// Source of truth for the Live and Case Analytics agents special-domain sets.

const DECISION_RESULT_PALETTE = [
  "#10B981", // green-500   → pass
  "#EF4444", // red-500     → fail
  "#F97316", // orange-500  → error
] as const;

const ENVIRONMENT_PALETTE = [
  "#10B981", // green-500  → live
  "#F59E0B", // yellow-500 → sandbox
] as const;

const CATEGORICAL_PALETTE = [
  "#6A63F1", // indigo-500
  "#10B981", // green-500
  "#F97316", // orange-500
  "#EC4899", // pink-500
  "#F59E0B", // yellow-500
  "#373D51", // gray-700
] as const;

export type SpecialDomain = {
  palette: readonly string[];
  // Not used by unbounded domains.
  values?: readonly string[];
  description: string;
};

// Live Analytics (flow decision data) domains.
export const LIVE_SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  decision_result: {
    palette: DECISION_RESULT_PALETTE,
    values: ["pass", "fail", "error"],
    description:
      "Use when the column carries the outcome of a decision. CAST the SQL column to one of the allowed values so the chart reads pass=green, fail=red, error=orange.",
  },
  environment: {
    palette: ENVIRONMENT_PALETTE,
    values: ["live", "sandbox"],
    description:
      "Use when the column carries the workspace environment. CAST to one of the allowed values so the chart reads live=green, sandbox=yellow.",
  },
  flow_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a flow identifier. The same flow reads as the same color across every chart.",
  },
  flow_version_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a flow version identifier. The same version reads as the same color across every chart.",
  },
};

// Case Analytics (case_history_events) domains.
export const CASE_SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  queue_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case queue identifier (e.g. `current_queue_id`, `queue_id`). Renders the queue name instead of the UUID on axes, legends, and tooltips, and keeps the same queue's color stable across charts.",
  },
  template_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case template identifier (`template_id`). Renders the template name instead of the UUID and keeps the same template's color stable across charts.",
  },
  template_version_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case template version identifier (`template_version_id`). Renders the version name instead of the UUID and keeps the same version's color stable across charts.",
  },
  user_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is any user identifier — an assignee or an actor (e.g. `current_assignee_id`, `user_id`, `assigned_to_user_id`, `assigned_by_user_id`, `unassigned_from_user_id`, `unassigned_by_user_id`, `archived_by_user_id`, `retried_by`). Renders the user's name instead of the UUID and keeps the same user's color stable across charts.",
  },
};

export const SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  ...LIVE_SPECIAL_DOMAINS,
  ...CASE_SPECIAL_DOMAINS,
};

export type AgentSpecialDomain = {
  name: string;
  description: string;
  values?: readonly string[];
};

export const specialDomainsForAgent = (
  domains: Record<string, SpecialDomain>,
): AgentSpecialDomain[] =>
  Object.entries(domains).map(([name, def]) => ({
    name,
    description: def.description,
    ...(def.values ? { values: def.values } : {}),
  }));

// Vega-Lite's top-level `config.font` cascades to most text marks (tick
// labels, legend labels, text marks) but axis titles, legend titles, and
// chart titles inherit through their own font chains, so they need to be
// set explicitly here too.
const APP_FONT = '"Inter Var", "Inter", sans-serif';

// Canvas text takes a numeric weight, not `font-variation-settings`, so the
// product's Inter Var 450/550 weights are passed through as numbers.
const FONT_WEIGHT_NORMAL = 450;

const CHART_FONT_SIZE = 11;

// gray scale (tailwind.config.js)
const GRAY_400 = "#9C9FAF";
const GRAY_500 = "#6B6F80";
const GRAY_800 = "#1F2537";

export const TAKTILE_CHART_CONFIG = {
  font: APP_FONT,
  axis: {
    labelFont: APP_FONT,
    labelFontSize: CHART_FONT_SIZE,
    labelFontWeight: FONT_WEIGHT_NORMAL,
    labelColor: GRAY_400,
    titleFont: APP_FONT,
    titleFontSize: CHART_FONT_SIZE,
    titleFontWeight: FONT_WEIGHT_NORMAL,
    titleColor: GRAY_500,
    tickColor: "#D1D3DB", // gray-300
    gridColor: "#E5E6EB", // gray-200
    domainColor: "#D1D3DB", // gray-300
  },
  legend: {
    labelFont: APP_FONT,
    labelFontSize: CHART_FONT_SIZE,
    labelFontWeight: FONT_WEIGHT_NORMAL,
    labelColor: GRAY_800,
    titleFont: APP_FONT,
    titleFontSize: CHART_FONT_SIZE,
    titleFontWeight: FONT_WEIGHT_NORMAL,
    titleColor: GRAY_500,
  },
  title: {
    font: APP_FONT,
    subtitleFont: APP_FONT,
  },
  header: {
    labelFont: APP_FONT,
    titleFont: APP_FONT,
  },
  mark: {
    font: APP_FONT,
  },
  // Rounds the value-end of bars. Geometric top only — VL doesn't round
  // negative bars at the bottom (vega/vega-lite#7586).
  bar: {
    cornerRadiusEnd: 2,
  },
  range: {
    category: CATEGORICAL_PALETTE,
  },
};
